"use client";

import L, { LatLngExpression } from "leaflet";
import { useEffect } from "react";
import { Polygon, useMap } from "react-leaflet";
import { OutputNode } from "@/types/real-estate";
import { thresholdForDesiredLevel } from "../map.constants";
import useMapZoom from "@/components/organisms/map/hooks/useMapZoom";
import { useBoundaries } from "@/hooks/useBoundaries";
import { administrativeLevelForCode } from "@/lib/boundaries/boundaryCache";
import { useMapContext } from "@/components/organisms/map/components/MapProvider";
import { useStableSearchParams } from "@/hooks/customSearchParams/useCustom";
import { findNodeAtom } from "./markers/useMarkers";
type Props = {
  realEstateNodes: OutputNode[];
};
export default function MapPolygons({
  realEstateNodes
}: Props) {
  const {
    zoom
  } = useMapZoom();
  const searchParams = useStableSearchParams();

  // In the People (demand) view, a seeker who matched via a broader zone (picked
  // Lisboa, searched Areeiro) is anchored on that broad node — drawing the tree
  // nodes would highlight all of Lisboa. When a location filter is active there,
  // highlight the SEARCHED zone(s) instead, so the region matches what you typed.
  // The seeker's real zone is still revealed (in violet) on click via DemandReveal.
  const locationParam = searchParams.get("location") ?? "";
  const filterCodes = locationParam.split(",").map(c => c.trim()).filter(Boolean);
  const useFilterHighlight = searchParams.get("listingType") === "demand" && filterCodes.length > 0;
  const visibleNodes = realEstateNodes.filter(location => !(zoom > thresholdForDesiredLevel[location.administrative_level] - 1));
  const polygonNodes = useFilterHighlight ? filterCodes.map(code => ({
    locationCode: code,
    administrative_level: administrativeLevelForCode(code)
  })) : visibleNodes;
  const geometryByCode = useBoundaries(polygonNodes);
  return <>
      {polygonNodes.map(location => {
      const geometry = geometryByCode.get(location.locationCode);
      if (!geometry) return null;
      return <Polygon key={location.locationCode} positions={convertPolygon(geometry)} pathOptions={{
        color: "#ffe8a8",
        weight: 1,
        fillOpacity: 0.2
      }} />;
    })}
      <DemandReveal realEstateNodes={realEstateNodes} data-sentry-element="DemandReveal" data-sentry-source-file="MapPolygons.tsx" />
    </>;
}

// When a demand seeker is selected (its pin or card), "bloom" all of the zones
// they target as polygons and frame the map to their combined extent. Only one
// seeker is ever revealed at a time; clearing the selection removes the bloom.
function DemandReveal({
  realEstateNodes
}: Props) {
  const map = useMap();
  const {
    forcedMapTarget
  } = useMapContext();
  const atom = forcedMapTarget ? findNodeAtom(realEstateNodes, forcedMapTarget.realEstateId) : null;
  const zones = atom?.isDemand ? atom.zones ?? [] : [];
  const geometryByCode = useBoundaries(zones.map(z => ({
    locationCode: z.locationCode,
    administrative_level: z.administrative_level
  })));
  const polygons = zones.map(z => ({
    code: z.locationCode,
    points: convertPolygon(geometryByCode.get(z.locationCode))
  })).filter(p => p.points.length > 0);
  useEffect(() => {
    if (polygons.length === 0) return;
    const group = L.featureGroup(polygons.map(p => L.polygon(p.points)));
    map.fitBounds(group.getBounds(), {
      padding: [48, 48],
      maxZoom: 13,
      animate: true
    });
    // Re-frame only when the revealed geometries change (new seeker selected).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometryByCode]);
  return <>
      {polygons.map(p => <Polygon key={p.code} positions={p.points} pathOptions={{
      color: "#7c3aed",
      weight: 2,
      fillColor: "#8b5cf6",
      fillOpacity: 0.18
    }} />)}
    </>;
}
export function convertPolygon(polygon?: string): LatLngExpression[] {
  if (!polygon) return [];
  try {
    let coordinates = JSON.parse(polygon);
    while (Array.isArray(coordinates) && coordinates.length > 0 && Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0])) {
      coordinates = coordinates[0];
    }
    return coordinates.map((point: number[]) => {
      if (Array.isArray(point) && point.length >= 2) {
        return [point[1], point[0]] as [number, number];
      }
      return null;
    }).filter((pt: unknown): pt is [number, number] => pt !== null);
  } catch (err) {
    console.error("Error parsing polygon:", err);
  }
  return [];
}